import type { InputFrame } from "../input";
import { nearestOnRail, railY, surfaceAt, type Rail } from "../level";
import type { Skater } from "../skater";
import { stickDir8 } from "../tricks";
import { airG, airGaps, enterAir } from "./air";
import { bail } from "./bail";
import { creditSpin } from "./landing";
import { headingUpRamp } from "./lip";
import { wrapAngle } from "./math";
import type { GrindState, SkaterEvent } from "./state";
import {
  COPING_BOOST,
  GRIND_COOLDOWN,
  GRIND_KEEP,
  GRIND_MIN_SPEED,
  GRIND_OUT_DEAD,
  GRIND_OUT_STEER,
  GRIND_POP_HOLD,
  GRIND_SLOPE_G,
  GRIND_SNAP_DIST,
  GRIND_SWITCH_NUDGE,
  OLLIE_V,
  SIDE_HOP_OUT,
  SIDE_HOP_V,
} from "./tuning";

/** The first rail a grind press would lock onto right now, and where along it; no side effects. */
function railToLock(sk: Skater, fromGround: boolean): { rail: Rail; t: number } | null {
  if (sk.grindCooldown > 0) return null;
  // Riding straight up a quarter, the coping is for a lip trick at the top, not a grind.
  const lipBound = fromGround && headingUpRamp(sk, surfaceAt(sk.level, sk.x, sk.z).quarter);
  const hop = fromGround ? null : sk.air?.hop;
  for (const rail of sk.level.rails) {
    if (lipBound && rail.kind === "coping") continue;
    const { t, dist } = nearestOnRail(rail, sk.x, sk.z);
    if (dist > GRIND_SNAP_DIST || t <= 0.01 || t >= 0.99) continue;
    // Hopped off to one side (#232): the rail you left, and any back that way, stay behind you.
    if (hop && railSide(rail, t, sk, hop) <= 0) continue;
    // From the ground you can pop up onto anything waist-high.
    const dy = sk.y - railY(rail, t);
    if (dy < (fromGround ? -1.0 : -0.45) || dy > 1.4) continue;
    return { rail, t };
  }
  return null;
}

/**
 * Whether pressing Grind now would lock onto a rail (rolling or in the air),
 * for the touch Grind button's glow (#67). The same search `tryGrind` does.
 */
export function railInReach(sk: Skater): boolean {
  if (sk.mode !== "ground" && sk.mode !== "air") return false;
  return railToLock(sk, sk.mode === "ground") !== null;
}

/**
 * Speed a coping caught from the air carries (#133): coming up a wall nearly
 * all of it is vertical, so take the whole velocity, plus what falling back to
 * the coping's height adds, or the ground speed at the lip if that's more
 * (a vert launch caps its pop), kept at `GRIND_KEEP`, plus `COPING_BOOST`.
 */
function copingCarry(sk: Skater, copingY: number): number {
  const fall = Math.max(0, sk.y - copingY);
  const v = Math.sqrt(sk.vx * sk.vx + sk.vy * sk.vy + sk.vz * sk.vz + 2 * airG(sk) * fall);
  return Math.max(v, sk.air?.lipSpeed ?? 0) * GRIND_KEEP + COPING_BOOST;
}

export function tryGrind(sk: Skater, input: InputFrame, fromGround: boolean): boolean {
  const found = railToLock(sk, fromGround);
  if (found) {
    const { rail, t } = found;
    const len = Math.hypot(rail.b.x - rail.a.x, rail.b.z - rail.a.z);
    const dx = (rail.b.x - rail.a.x) / len;
    const dz = (rail.b.z - rail.a.z) / len;
    const f = sk.forward;
    const hv = fromGround ? (f.x * dx + f.z * dz) * sk.speed : sk.vx * dx + sk.vz * dz;
    const facing = f.x * dx + f.z * dz;
    const dir: 1 | -1 = Math.abs(hv) > 0.3 ? (hv >= 0 ? 1 : -1) : facing >= 0 ? 1 : -1;
    // Redirect momentum along the rail (THPS) instead of keeping only the
    // component that happens to point along it, which bled speed on angled hits.
    const total = fromGround ? Math.abs(sk.speed) : Math.hypot(sk.vx, sk.vz);
    const speed = Math.max(
      Math.abs(hv),
      total * GRIND_KEEP,
      !fromGround && rail.kind === "coping" ? copingCarry(sk, railY(rail, t)) : 0,
      GRIND_MIN_SPEED,
    );

    // Popping onto a rail while a landing waits for a manual is a new line.
    if (sk.linkWindow > 0) sk.bankCombo();
    if (sk.mode === "air") creditSpin(sk);
    const special = sk.pullSpecial("grind");
    const trick = special?.kind === "grind" ? special : sk.tricks.grind[stickDir8(input.moveX, input.moveY)];
    const index = sk.addTrick(trick.name, trick.points);
    if (sk.mode === "air") airGaps(sk, "grind");

    // Keep whichever end of the board was already leading.
    const travelYaw = Math.atan2(dx * dir, dz * dir);
    sk.yaw = Math.abs(wrapAngle(travelYaw - sk.yaw)) <= Math.PI / 2 ? travelYaw : travelYaw + Math.PI;

    const popQueued = (sk.air?.popPress ?? 0) > 0;
    sk.mode = "grind";
    if (sk.manual) sk.manualCarry = sk.manual.balance;
    sk.manual = null;
    sk.air = null;
    sk.grindBuffer = 0;
    sk.normal = { x: 0, y: 1, z: 0 };
    sk.grind = {
      rail,
      t,
      dir,
      speed,
      trick,
      index,
      balance: sk.balanceCarry ?? (sk.rng() - 0.5) * 0.3,
      balanceVel: 0,
      time: 0,
      popQueued,
    };
    placeOnRail(sk);
    return true;
  }
  return false;
}

export function placeOnRail(sk: Skater): void {
  const g = sk.grind!;
  sk.x = g.rail.a.x + (g.rail.b.x - g.rail.a.x) * g.t;
  sk.z = g.rail.a.z + (g.rail.b.z - g.rail.a.z) * g.t;
  sk.y = railY(g.rail, g.t);
}

export function stepGrind(sk: Skater, input: InputFrame, dt: number, events: SkaterEvent[]): void {
  const g = sk.grind!;
  g.time += dt;
  sk.combo.addPoints(g.index, g.trick.perSecond * dt);
  sk.crouch += (0.45 - sk.crouch) * Math.min(1, dt * 10);

  const len = Math.hypot(g.rail.b.x - g.rail.a.x, g.rail.b.z - g.rail.a.z);
  const dx = (g.rail.b.x - g.rail.a.x) / len;
  const dz = (g.rail.b.z - g.rail.a.z) / len;
  // Rise per metre along the way you're going: a handrail pulls you down it
  // like a ramp does (up one it slows you, never below a grind's minimum).
  const rise = (g.dir * ((g.rail.yb ?? g.rail.y) - g.rail.y)) / len;
  // Up a chain lift (#189) the chain carries you and holds you steady.
  const lift = rise > 0 && g.rail.lift !== undefined ? g.rail.lift : 0;

  // Balance: an unstable needle you hold steady with left/right.
  if (!lift) sk.balanceTime += dt;
  if (lift) {
    g.balance *= 1 - Math.min(1, 2 * dt);
    g.balanceVel = 0;
  } else if (sk.stepBalance(g, input.moveX, dt, sk.wobble("grind"))) {
    bail(sk, events);
    return;
  }

  if (input.pressed.grind) switchGrind(sk, g, input);

  if (rise !== 0) g.speed = Math.max(GRIND_MIN_SPEED, lift, g.speed - GRIND_SLOPE_G * rise * dt);

  const hop = input.pressed.spinL ? "spinL" : input.pressed.spinR ? "spinR" : null;
  const ollie = input.pressed.ollie || (g.popQueued && g.time >= GRIND_POP_HOLD);
  if (ollie || hop) {
    // Off to the side of the way you're going (#232): the right of travel is (-z, x).
    const tx = dx * g.dir;
    const tz = dz * g.dir;
    const steer = Math.abs(input.moveX) > GRIND_OUT_DEAD
      ? (Math.sign(input.moveX) * (Math.abs(input.moveX) - GRIND_OUT_DEAD) * GRIND_OUT_STEER) / (1 - GRIND_OUT_DEAD)
      : 0;
    const side = (hop === "spinR" ? SIDE_HOP_OUT : hop === "spinL" ? -SIDE_HOP_OUT : 0) + steer;
    const up = hop && !ollie ? SIDE_HOP_V : OLLIE_V * 0.9;
    sk.balanceCarry = g.balance;
    sk.grindCooldown = GRIND_COOLDOWN;
    const air = enterAir(sk, tx * g.speed - tz * side, up * sk.special.boost * sk.scale("ollie"), tz * g.speed + tx * side);
    if (hop) air.hop = { sx: -tz * Math.sign(side || 1), sz: tx * Math.sign(side || 1), button: hop };
    return;
  }

  g.t += (g.dir * g.speed * dt) / len;
  if ((g.t <= 0 || g.t >= 1) && g.rail.joined && carryOn(sk, g, len)) {
    placeOnRail(sk);
    return;
  }
  if (g.t <= 0 || g.t >= 1) {
    g.t = Math.max(0, Math.min(1, g.t));
    sk.balanceCarry = g.balance;
    placeOnRail(sk);
    sk.grindCooldown = GRIND_COOLDOWN;
    enterAir(sk, dx * g.dir * g.speed, 1 + rise * g.speed, dz * g.dir * g.speed);
    return;
  }
  placeOnRail(sk);
}

/** How far the rail's point at `t` is out along a side hop's way (m): at or below 0 it's back where you hopped from. */
function railSide(rail: Rail, t: number, sk: Skater, hop: { sx: number; sz: number }): number {
  const px = rail.a.x + (rail.b.x - rail.a.x) * t;
  const pz = rail.a.z + (rail.b.z - rail.a.z) * t;
  return (px - sk.x) * hop.sx + (pz - sk.z) * hop.sz;
}

/** Where two joined rails meet: ends this close (m) are one point. */
const JOIN_EPS = 1e-3;

/**
 * Off the end of a joined rail (#187), onto the joined rail that starts
 * there, with the distance left over: the same grind (trick, speed, balance)
 * round a curve laid as short straights. False when nothing joins on.
 */
function carryOn(sk: Skater, g: GrindState, len: number): boolean {
  const atB = g.t >= 1;
  const end = atB ? g.rail.b : g.rail.a;
  const endY = atB ? railY(g.rail, 1) : g.rail.y;
  const over = (atB ? g.t - 1 : -g.t) * len;
  for (const r of sk.level.rails) {
    if (!r.joined || r === g.rail) continue;
    const fromA = Math.hypot(r.a.x - end.x, r.a.z - end.z) < JOIN_EPS && Math.abs(r.y - endY) < JOIN_EPS;
    const fromB = !fromA && Math.hypot(r.b.x - end.x, r.b.z - end.z) < JOIN_EPS && Math.abs(railY(r, 1) - endY) < JOIN_EPS;
    if (!fromA && !fromB) continue;
    const old = Math.atan2((g.rail.b.x - g.rail.a.x) * g.dir, (g.rail.b.z - g.rail.a.z) * g.dir);
    const ahead = Math.abs(wrapAngle(old - sk.yaw)) <= Math.PI / 2;
    const rl = Math.hypot(r.b.x - r.a.x, r.b.z - r.a.z);
    g.rail = r;
    g.dir = fromA ? 1 : -1;
    g.t = Math.min(0.999, Math.max(0.001, fromA ? over / rl : 1 - over / rl));
    // Keep whichever end of the board was leading.
    const travel = Math.atan2((r.b.x - r.a.x) * g.dir, (r.b.z - r.a.z) * g.dir);
    sk.yaw = ahead ? travel : travel + Math.PI;
    return true;
  }
  return false;
}

/**
 * Extended grinds (THPS 3+4): grind + a direction other than the current
 * grind's switches to that grind mid-rail. It's a new combo entry (repeat
 * decay applies), the rider keeps the rail, speed and balance needle, and the
 * needle gets a small kick the way it was leaning. The press is used up, so
 * it can't re-lock the rail after you leave it.
 */
export function switchGrind(sk: Skater, g: GrindState, input: InputFrame): void {
  const trick = sk.tricks.grind[stickDir8(input.moveX, input.moveY)];
  if (trick === g.trick) return;
  sk.grindBuffer = 0;
  g.trick = trick;
  g.index = sk.addTrick(trick.name, trick.points);
  g.balanceVel += GRIND_SWITCH_NUDGE * (g.balance < 0 ? -1 : 1);
}
